import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {DashboardBackendBrainPart, DashboardBrainPart} from '../../symbols/dashboard-brain-parts-general.symbols';
import {RtPlatformService} from '../../../rt-platform/rt-platform.service';
import {unifiedSerializer} from '../../../shared/unified-serializer';

@Injectable()
export class DashboardBrainPartsService {
  constructor(private http: HttpClient, private platform: RtPlatformService) {}

  /**
   * Serializes the backend brain part to the frontend brain part.
   */
  static dashboardBrainPartSerializer(brainPart: DashboardBackendBrainPart): DashboardBrainPart {
    return unifiedSerializer<DashboardBackendBrainPart, DashboardBrainPart>(brainPart, {
      id: {
        propertyToWrite: 'id',
        serializeMethod: id => id,
      },
      name: {
        propertyToWrite: 'name',
        serializeMethod: name => name,
      },
      hide_related_chart: {
        propertyToWrite: 'shouldHideRelatedChart',
        serializeMethod: hideNeeded => hideNeeded,
      },
      display_parent_chart: {
        propertyToWrite: 'shouldDisplayParentChart',
        serializeMethod: displayNeeded => displayNeeded,
      },
      get_statistic_from_brain_section: {
        propertyToWrite: 'useBrainPartStatistic',
        serializeMethod: useBrainPartStatistic => useBrainPartStatistic,
      },
      view_type: {
        propertyToWrite: 'viewType',
        serializeMethod: viewType => viewType,
      },
      chart_type: {
        propertyToWrite: 'chartType',
        serializeMethod: chartType => chartType,
      },
      view_value_for_table: {
        propertyToWrite: 'viewValueForTable',
        serializeMethod: viewValue => viewValue,
      },
      mri_labels_to_tree_brain_section: {
        propertyToWrite: 'relatedSurfaceRegionData',
        serializeMethod: surfaceRegionsData =>
          // Extract complex backend structure to the flat frontend structure.
          surfaceRegionsData.map(surfaceRegion => ({
            name: surfaceRegion.mri_label.name,
            surfaceLabel: surfaceRegion.mri_label.surface_label,
            colorType: surfaceRegion.color_type,
            colorTypical: surfaceRegion.color_typical,
            colorAtypical: surfaceRegion.color_atypical,
            atypicalCondition: surfaceRegion.mri_label.atypical_condition,
            scanLabels: surfaceRegion.mri_label.scan_labels,
            relatedBrainPart: surfaceRegion.tree_brain_section,
          })),
      },
      scan_source: {
        propertyToWrite: 'requiredScanType',
        serializeMethod: scanSource => scanSource,
      },
      loading_priority: {
        propertyToWrite: 'loadingPriority',
        serializeMethod: priority => priority,
      },
      subregions: {
        propertyToWrite: 'childRegions',
        serializeMethod: childRegions => childRegions.map(DashboardBrainPartsService.dashboardBrainPartSerializer),
      },
      versions: {
        propertyToWrite: 'compatibleKeyLayerVersions',
        serializeMethod: versions => versions,
      },
      short_about: {
        propertyToWrite: 'shortAbout',
        serializeMethod: shortAbout => shortAbout,
      },
      detailed_about: {
        propertyToWrite: 'detailedAbout',
        serializeMethod: about => about,
      },
      use_root_part_about: {
        propertyToWrite: 'useRootPartAbout',
        serializeMethod: useRootPartAbout => useRootPartAbout,
      }
    });
  }

  /**
   * Provides a list of brain parts and their structure.
   */
  public loadBrainParts(): Observable<DashboardBrainPart[]> {
    const url = `${this.platform.baseUrl}/api/public-brain-section-structure/`;

    return this.http.get<DashboardBackendBrainPart[]>(url).pipe(
      // Map the backend brain parts to the frontend brain parts.
      map(brainParts => brainParts.map(DashboardBrainPartsService.dashboardBrainPartSerializer))
    );
  }
}
